/**
 * Email Store (Zustand)
 *
 * Manages email state: connections, email list, current email/thread, UI state.
 * All API calls include the JWT from auth-store (in-memory, zero-knowledge).
 *
 * Optimistic updates: mark read/star/trash update UI immediately,
 * then revert on API failure.
 */

import { create } from 'zustand';
import { emailApi, providerConnectionsApi } from '../lib/email-api';
import type { NormalizedEmail, NormalizedThread } from '@signa/shared/types';
import type { EmailDraft } from '../types/email';
import { DEMO_EMAILS } from './demoData';
import { useAuthStore } from './authStore';

const DEMO_TOKEN = 'demo-token';

function isDemoMode(): boolean {
  return useAuthStore.getState().token === DEMO_TOKEN;
}

export interface CachedEmail {
  id: string;
  provider: 'gmail' | 'microsoft';
  threadId: string;
  subject: string;
  fromName: string;
  fromEmail: string;
  toRecipients: Array<{ name: string; email: string }>;
  ccRecipients?: Array<{ name: string; email: string }>;
  snippet: string;
  receivedAt: string;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  labels: string[];
  // Extended fields used by EmailPanel (Sprint 1.3 legacy + Sprint 2.5)
  isArchived?: boolean;
  isSnoozed?: boolean;
  snoozeUntil?: string;
  category?: string;
  isUnread?: boolean; // inverse of isRead, used in EmailPanel sort/filter
  listUnsubscribe?: { url?: string; mailto?: string; oneClick?: boolean };
}

function mergeFetchedEmailPage(
  existing: CachedEmail[],
  fetched: CachedEmail[],
  provider: 'gmail' | 'microsoft',
  archived: boolean,
  threadId?: string
): CachedEmail[] {
  const fetchedIds = new Set(fetched.map((email) => email.id));
  const retained = existing.filter((email) => {
    if (fetchedIds.has(email.id)) return false;
    if (email.provider !== provider) return true;
    if (threadId) return email.threadId !== threadId;
    return Boolean(email.isArchived) !== archived;
  });

  return [...retained, ...fetched].sort(
    (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
  );
}

export type SyncStatus = 'idle' | 'syncing' | 'error';

export interface EmailConnection {
  provider: 'gmail' | 'microsoft';
  connected: boolean;
  emailAddress?: string;
  syncStatus: SyncStatus;
  lastSyncAt: string | null;
  connectionId?: string;
}

interface EmailState {
  // Connection status
  connections: EmailConnection[];

  // Email data - flat list of individual emails from D1 cache
  emails: CachedEmail[];
  currentEmail: NormalizedEmail | null;
  currentThread: NormalizedThread | null;

  // Active provider
  activeProvider: 'gmail' | 'microsoft' | null;

  // UI state
  selectedEmailId: string | null;
  selectedThreadId: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  nextPageToken: string | null;

  // Actions
  loadConnections: () => Promise<void>;
  loadEmails: (provider: 'gmail' | 'microsoft', threadId?: string, archived?: boolean) => Promise<void>;
  loadMoreEmails: (provider: 'gmail' | 'microsoft') => Promise<void>;
  loadEmail: (id: string) => Promise<void>;
  loadThread: (threadId: string) => Promise<void>;
  triggerSync: (provider: 'gmail' | 'microsoft', strategy: 'full' | 'delta' | 'historical', days?: number) => Promise<void>;
  markRead: (emailId: string, read: boolean) => Promise<void>;
  markStarred: (emailId: string, starred: boolean) => Promise<void>;
  trashEmail: (emailId: string) => Promise<void>;
  archiveEmail: (emailId: string) => Promise<void>;
  sendEmail: (draft: EmailDraft) => Promise<{ messageId: string }>;
  replyToEmail: (threadId: string, draft: EmailDraft) => Promise<{ messageId: string }>;
  connectProvider: (provider: 'gmail' | 'microsoft') => Promise<void>;
  disconnectProvider: (provider: 'gmail' | 'microsoft') => Promise<void>;
  setActiveProvider: (provider: 'gmail' | 'microsoft') => void;
  clearError: () => void;
  reset: () => void;
  loadDemo: () => void;
}

const initialState = {
  connections: [] as EmailConnection[],
  emails: [] as CachedEmail[],
  currentEmail: null as NormalizedEmail | null,
  currentThread: null as NormalizedThread | null,
  activeProvider: null as 'gmail' | 'microsoft' | null,
  selectedEmailId: null as string | null,
  selectedThreadId: null as string | null,
  isLoading: false,
  isSyncing: false,
  error: null as string | null,
  nextPageToken: null as string | null,
};

export const useEmailStore = create<EmailState>((set, get) => ({
  ...initialState,

  /**
   * Load provider connections from API.
   */
  loadConnections: async () => {
    if (isDemoMode()) {
      get().loadDemo();
      return;
    }

    try {
      const data = await providerConnectionsApi.list();
      const connections: EmailConnection[] = data.connections.map((conn) => ({
        provider: conn.provider,
        connected: conn.isActive,
        emailAddress: conn.email,
        syncStatus: conn.syncStatus ?? 'idle',
        lastSyncAt: conn.lastSyncAt,
        connectionId: conn.id,
      }));
      set({ connections });

      // Auto-select first connected provider
      const active = get().activeProvider;
      if (!active && connections.length > 0) {
        const firstConnected = connections.find((c) => c.connected);
        if (firstConnected) {
          set({ activeProvider: firstConnected.provider });
        }
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load connections' });
    }
  },

  /**
   * Load emails for a provider (first page).
   */
  loadEmails: async (provider: 'gmail' | 'microsoft', threadId?: string, archived = false) => {
    if (isDemoMode()) {
      if (get().emails.length === 0) get().loadDemo();
      set({ activeProvider: provider, isLoading: false, error: null });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const data = await emailApi.list(provider, { threadId, pageSize: 50, archived });
      const emails = mergeFetchedEmailPage(get().emails, data.emails, provider, archived, threadId);
      set({
        emails,
        nextPageToken: data.nextPageToken ?? null,
        isLoading: false,
        activeProvider: provider,
      });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load emails' });
    }
  },

  /**
   * Load more emails (pagination).
   */
  loadMoreEmails: async (provider: 'gmail' | 'microsoft') => {
    const { nextPageToken, emails, isLoading } = get();
    if (!nextPageToken || isLoading) return;

    set({ isLoading: true });
    try {
      const data = await emailApi.list(provider, {
        pageSize: 50,
        pageToken: nextPageToken,
      });
      set({
        emails: [...emails, ...data.emails],
        nextPageToken: data.nextPageToken ?? null,
        isLoading: false,
      });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load more emails' });
    }
  },

  /**
   * Load single email (decrypted).
   */
  loadEmail: async (id: string) => {
    set({ isLoading: true, error: null, selectedEmailId: id });
    try {
      const data = await emailApi.get(id);
      set({ currentEmail: data.email, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load email' });
    }
  },

  /**
   * Load full thread.
   */
  loadThread: async (threadId: string) => {
    set({ isLoading: true, error: null, selectedThreadId: threadId });
    try {
      const data = await emailApi.getThread(threadId);
      set({ currentThread: data.thread, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load thread' });
    }
  },

  /**
   * Trigger email sync.
   */
  triggerSync: async (provider: 'gmail' | 'microsoft', strategy: 'full' | 'delta' | 'historical', days?: number) => {
    if (isDemoMode()) {
      if (get().emails.length === 0) get().loadDemo();
      return;
    }

    // Optimistic update
    set((s) => ({
      isSyncing: true,
      connections: s.connections.map((c) =>
        c.provider === provider ? { ...c, syncStatus: 'syncing' as SyncStatus } : c
      ),
      error: null,
    }));
    try {
      await emailApi.sync(provider, strategy, days);
      // Sync runs async; reload connections after a delay to get updated status
      setTimeout(() => {
        void (async () => {
          await get().loadConnections();
          await get().loadEmails(provider);
          set({ isSyncing: false });
        })();
      }, 2000);
    } catch (err) {
      set((s) => ({
        isSyncing: false,
        error: err instanceof Error ? err.message : 'Sync failed',
        connections: s.connections.map((c) =>
          c.provider === provider
            ? { ...c, syncStatus: 'error' as SyncStatus }
            : c
        ),
      }));
    }
  },

  /**
   * Mark email as read/unread.
   * Optimistic update - reverts on failure.
   */
  markRead: async (emailId: string, read: boolean) => {
    const prevEmails = get().emails;
    const prevCurrentEmail = get().currentEmail;
    // Optimistic
    set((s) => ({
      emails: s.emails.map((e) =>
        e.id === emailId ? { ...e, isRead: read } : e
      ),
      currentEmail: s.currentEmail
        ? { ...s.currentEmail, isRead: read }
        : null,
    }));
    try {
      await emailApi.update(emailId, { isRead: read });
    } catch {
      // Revert on failure
      set({ emails: prevEmails, currentEmail: prevCurrentEmail });
    }
  },

  /**
   * Mark email as starred/unstarred.
   * Optimistic update - reverts on failure.
   */
  markStarred: async (emailId: string, starred: boolean) => {
    const prevEmails = get().emails;
    const prevCurrentEmail = get().currentEmail;
    // Optimistic
    set((s) => ({
      emails: s.emails.map((e) =>
        e.id === emailId ? { ...e, isStarred: starred } : e
      ),
      currentEmail: s.currentEmail
        ? { ...s.currentEmail, isStarred: starred }
        : null,
    }));
    try {
      await emailApi.update(emailId, { isStarred: starred });
    } catch {
      // Revert on failure
      set({ emails: prevEmails, currentEmail: prevCurrentEmail });
    }
  },

  /**
   * Trash email (move to trash, optimistic).
   */
  trashEmail: async (emailId: string) => {
    const prevEmails = get().emails;
    const prevCurrentEmail = get().currentEmail;
    const prevSelectedEmailId = get().selectedEmailId;
    // Optimistic remove
    set((s) => ({
      emails: s.emails.filter((e) => e.id !== emailId),
      currentEmail: s.currentEmail?.id === emailId ? null : s.currentEmail,
      selectedEmailId: s.selectedEmailId === emailId ? null : s.selectedEmailId,
    }));
    try {
      await emailApi.delete(emailId);
    } catch {
      const message = 'Failed to delete email';
      // Revert
      set({
        emails: prevEmails,
        currentEmail: prevCurrentEmail,
        selectedEmailId: prevSelectedEmailId,
        error: message,
      });
      throw new Error(message);
    }
  },

  /**
   * Archive email.
   * Optimistic - marks as archived in local state.
   */
  archiveEmail: async (emailId: string) => {
    const prevEmails = get().emails;
    // Optimistic: mark archived so Inbox filters it out and Archive can show it immediately.
    set((s) => ({
      emails: s.emails.map((e) =>
        e.id === emailId ? { ...e, isArchived: true } : e
      ),
      currentEmail: s.currentEmail
        ? { ...s.currentEmail, isArchived: true }
        : null,
    }));
    try {
      await emailApi.archive(emailId);
    } catch {
      const message = 'Failed to archive email';
      set({ emails: prevEmails, error: message });
      throw new Error(message);
    }
  },

  /**
   * Send a new email.
   */
  sendEmail: async (draft: EmailDraft): Promise<{ messageId: string }> => {
    set({ error: null });
    try {
      const provider = get().activeProvider;
      if (!provider) throw new Error('Connect an email provider before sending.');
      const result = await emailApi.send({
        provider,
        to: draft.to,
        cc: draft.cc,
        bcc: draft.bcc,
        subject: draft.subject,
        bodyText: draft.bodyText,
        bodyHtml: draft.bodyHtml,
      });
      return result;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to send email' });
      throw err;
    }
  },

  /**
   * Reply to a thread.
   */
  replyToEmail: async (threadId: string, draft: EmailDraft): Promise<{ messageId: string }> => {
    set({ error: null });
    try {
      const provider = get().activeProvider;
      if (!provider) throw new Error('Connect an email provider before replying.');
      const result = await emailApi.reply(threadId, {
        provider,
        bodyText: draft.bodyText,
        bodyHtml: draft.bodyHtml,
        cc: draft.cc,
        bcc: draft.bcc,
      });
      return result;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to send reply' });
      throw err;
    }
  },

  /**
   * Connect a new email provider via OAuth.
   * Redirects to OAuth URL.
   */
  connectProvider: async (provider: 'gmail' | 'microsoft') => {
    try {
      const { url } = await providerConnectionsApi.initiateOAuth(provider);
      window.location.href = url;
    } catch {
      // Fallback: redirect directly
      const url =
        provider === 'gmail'
          ? `/oauth/gmail`
          : `/oauth/microsoft`;
      window.location.href = url;
    }
  },

  /**
   * Disconnect a provider.
   */
  disconnectProvider: async (provider: 'gmail' | 'microsoft') => {
    const conn = get().connections.find((c) => c.provider === provider && c.connected);
    if (!conn?.connectionId) return;

    try {
      await providerConnectionsApi.disconnect(conn.connectionId);
      set((s) => ({
        connections: s.connections.map((c) =>
          c.provider === provider ? { ...c, connected: false } : c
        ),
        // If disconnecting active provider, switch
        activeProvider:
          s.activeProvider === provider
            ? s.connections.find((c) => c.provider !== provider && c.connected)?.provider ?? null
            : s.activeProvider,
        emails: s.activeProvider === provider ? [] : s.emails,
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to disconnect' });
    }
  },

  /**
   * Set the active provider.
   */
  setActiveProvider: (provider: 'gmail' | 'microsoft') => {
    set({ activeProvider: provider, emails: [], currentEmail: null, currentThread: null });
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),

  loadDemo: () => {
    set({
      emails: DEMO_EMAILS,
      connections: [
        { provider: 'gmail' as const, connected: true, emailAddress: 'demo@gmail.com', syncStatus: 'idle' as const, lastSyncAt: new Date().toISOString() },
        { provider: 'microsoft' as const, connected: true, emailAddress: 'demo@outlook.com', syncStatus: 'idle' as const, lastSyncAt: new Date().toISOString() },
      ],
      activeProvider: 'gmail',
      selectedEmailId: null,
      selectedThreadId: null,
      isLoading: false,
      isSyncing: false,
      error: null,
    });
  },
}));

// Demo Mode
if (typeof window !== 'undefined') {
  window.addEventListener('signa:demo-mode', () => {
    const state = useEmailStore.getState();
    if (state.emails.length === 0) {
      useEmailStore.getState().loadDemo();
    }
  });
}
